/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Modal,
  Form,
  Row,
  Col,
  Input,
  DatePicker,
  Button,
  Table,
  Space,
  Typography,
  message,
  Spin,
  Flex,
  Popconfirm,
} from "antd";
import { PlusOutlined, EditOutlined, ArrowLeftOutlined, DeleteOutlined } from "@ant-design/icons";
import dayjs from "dayjs";

// อ่านอย่างเดียว: ใช้ของเดิมจาก Delivery Note (ERP) ที่มีอยู่แล้ว ห้ามแก้ไฟล์เหล่านี้
import DeliveryNoteService from "../../../../service/ERP/DeliveryNote.service";
import JobOrderService from "../../../../service/ERP/JobOrder.service";
import { detailColumns } from "../../../../pages/ERP/delivery-note/model";
import ModalJOItems from "./ModalJOItems";

const delService = DeliveryNoteService();
const joService = JobOrderService();

/**
 * Modal พิเศษสำหรับเปิด/สร้าง Delivery Note จากหน้า Job Order เท่านั้น
 * - แสดงผลและสร้างข้อมูลโดยจำกัดเฉพาะ jocode ที่ส่งเข้ามาเท่านั้น
 * - ถ้ายังไม่มี Delivery Note ของ jocode นี้ -> เปิดฟอร์มสร้างใหม่ (ล็อค JO)
 * - ถ้ามีอยู่แล้ว -> แสดงรายการที่มี พร้อมปุ่มแก้ไข (ไปหน้า Delivery Note เดิม) และปุ่มสร้างเพิ่ม (โหลดใหม่)
 * หมายเหตุ: ไม่มีการแก้ไขไฟล์ของ Delivery Note เดิมใด ๆ ทั้งระบบเก่าและระบบ ERP
 */
export default function ModalJODeliveryNote({ show, close, jocode }) {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState("list"); // "list" | "create"
  const [dnList, setDnList] = useState([]);
  const [joInfo, setJoInfo] = useState({});
  const [joItemsData, setJoItemsData] = useState([]);
  const [detail, setDetail] = useState([]);
  const [openJOItemsModal, setOpenJOItemsModal] = useState(false);

  const resetCreateForm = () => {
    form.resetFields();
    setDetail([]);
  };

  const startCreate = (latestJo) => {
    const info = latestJo || joInfo;
    resetCreateForm();
    form.setFieldsValue({
      jocode,
      cuscode: info?.cuscode,
      cusname: info?.cusname,
      brand: info?.brand,
    });
    setMode("create");
  };

  const loadData = async () => {
    if (!jocode) return;
    setLoading(true);
    try {
      const [dnRes, joRes] = await Promise.all([
        delService.search({ jocode }),
        joService.get(jocode),
      ]);
      const dnRows = dnRes?.data?.data || [];
      const joResult = joRes?.data?.result || [];
      const latest = joResult[joResult.length - 1] || {};

      const info = {
        cuscode: latest.cuscode,
        cusname: latest.cusname,
        brand: latest.brand,
      };
      setJoInfo(info);
      setJoItemsData(latest.detail || []);
      setDnList(dnRows);

      if (dnRows.length === 0) {
        startCreate(info);
      } else {
        setMode("list");
      }
    } catch (err) {
      console.log(err);
      message.error("โหลดข้อมูลไม่สำเร็จ");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (show && jocode) {
      loadData();
    }
    if (!show) {
      setMode("list");
      setDnList([]);
      setJoItemsData([]);
      resetCreateForm();
    }
  }, [show, jocode]);

  const updateDetail = (index, field, value) => {
    setDetail((prev) =>
      prev.map((item, i) => (i === index ? { ...item, [field]: value } : item))
    );
  };

  const deleteDetail = (seq) => {
    setDetail((prev) =>
      prev.filter((d) => d.seq !== seq).map((d, i) => ({ ...d, seq: i + 1 }))
    );
  };

  const handleConfirmJOItems = (selectedItems) => {
    setDetail((prev) => {
      const today = dayjs();
      const newItems = selectedItems.map((d) => {
        const shelfLife = Number(d.shelf_life || 0) > 0 ? Number(d.shelf_life) : 60;
        return {
          seq: 0,
          pg_code: d.pg_code,
          pg_name: d.pg_name,
          production_date: today,
          expiry_date: today.add(shelfLife, "day"),
          carton: d.carton || 0,
          detail_remark: "",
        };
      });
      return [...prev, ...newItems].map((d, i) => ({ ...d, seq: i + 1 }));
    });
    setOpenJOItemsModal(false);
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      if (detail.length === 0) {
        message.warning("กรุณาเพิ่มรายการสินค้า");
        return;
      }
      const payload = {
        header: {
          ...values,
          dlvdate: values.dlvdate ? dayjs(values.dlvdate).format("YYYY-MM-DD") : null,
        },
        detail: detail.map((d) => ({
          ...d,
          production_date: d.production_date ? dayjs(d.production_date).format("YYYY-MM-DD") : null,
          expiry_date: d.expiry_date ? dayjs(d.expiry_date).format("YYYY-MM-DD") : null,
        })),
      };
      await delService.create(payload);
      message.success("บันทึกสำเร็จ");
      const dnRes = await delService.search({ jocode });
      setDnList(dnRes?.data?.data || []);
      setMode("list");
    } catch (err) {
      if (err?.errorFields) return;
      message.error(err?.response?.data?.message || "บันทึกไม่สำเร็จ");
    }
  };

  const handleEditExisting = (record) => {
    close();
    navigate("/ERP/delivery-note/manage/edit", {
      state: {
        config: {
          title: "Edit Delivery Note",
          action: "edit",
          code: record?.delcode,
        },
      },
    });
  };

  const handleDeleteDN = async (record) => {
    try {
      await delService.deleted(record?.delcode);
      message.success("ลบสำเร็จ");
      const dnRes = await delService.search({ jocode });
      const rows = dnRes?.data?.data || [];
      setDnList(rows);
      if (rows.length === 0) {
        startCreate();
      }
    } catch (err) {
      console.log(err);
      message.error("ลบไม่สำเร็จ");
    }
  };

  const totalCarton = detail.reduce((s, d) => s + Number(d.carton || 0), 0);
  const detailCols = detailColumns({ updateDetail, deleteDetail });

  const dnColumns = [
    { title: "เลขที่ใบส่งมอบ", dataIndex: "delcode", key: "delcode", width: 150 },
    {
      title: "โหลด",
      dataIndex: "load_no",
      key: "load_no",
      width: 90,
      align: "center",
      render: (v) => `โหลดที่ ${v}`,
    },
    {
      title: "วันที่ส่งมอบ",
      dataIndex: "dlvdate",
      key: "dlvdate",
      width: 120,
      align: "center",
      render: (v) => (v ? dayjs(v).format("DD/MM/YYYY") : "-"),
    },
    {
      title: "จำนวน/กล่อง",
      dataIndex: "total_carton",
      key: "total_carton",
      width: 110,
      align: "right",
      render: (v) => Number(v || 0).toLocaleString(),
    },
    {
      title: "ตัวเลือก",
      key: "action",
      width: 120,
      align: "center",
      render: (_, record) => (
        <Space>
          <Button
            size="small"
            icon={<EditOutlined />}
            className="bn-primary-outline"
            onClick={() => handleEditExisting(record)}
          />
          <Popconfirm
            title="ยืนยันการลบ?"
            onConfirm={() => handleDeleteDN(record)}
            okText="ลบ"
            cancelText="ยกเลิก"
          >
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Modal
      open={show}
      onCancel={close}
      maskClosable={false}
      width={950}
      style={{ top: 20 }}
      title={`Delivery Note - JO: ${jocode || ""}`}
      footer={
        mode === "create" ? (
          <Space>
            {dnList.length > 0 && (
              <Button icon={<ArrowLeftOutlined />} onClick={() => setMode("list")}>
                กลับไปดูรายการ
              </Button>
            )}
            <Button onClick={close}>ปิด</Button>
            <Button type="primary" onClick={handleSave}>
              บันทึก
            </Button>
          </Space>
        ) : (
          <Space>
            <Button onClick={close}>ปิด</Button>
          </Space>
        )
      }
    >
      <Spin spinning={loading}>
        {mode === "list" ? (
          <Space direction="vertical" size="middle" style={{ display: "flex" }}>
            <Flex justify="space-between" align="center">
              <Typography.Text>
                ลูกค้า: <strong>{joInfo.cusname || "-"}</strong> &nbsp;&nbsp; Brand:{" "}
                <strong>{joInfo.brand || "-"}</strong>
              </Typography.Text>
              <Button type="primary" icon={<PlusOutlined />} onClick={() => startCreate()}>
                สร้างใบส่งมอบใหม่
              </Button>
            </Flex>
            <Table
              size="small"
              rowKey="delcode"
              columns={dnColumns}
              dataSource={dnList}
              pagination={false}
            />
          </Space>
        ) : (
          <Space direction="vertical" size="middle" style={{ display: "flex" }}>
            <Form form={form} layout="vertical" autoComplete="off">
              <Row gutter={[16, 0]}>
                <Col xs={24} sm={8} md={6}>
                  <Form.Item label="Job Order" name="jocode">
                    <Input disabled />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={8} md={5}>
                  <Form.Item label="โหลดที่" name="load_no">
                    <Input placeholder="ระบุโหลดที่" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={8} md={6}>
                  <Form.Item
                    label="วันที่ส่งมอบ"
                    name="dlvdate"
                    rules={[{ required: true, message: "กรุณาเลือกวันที่" }]}
                  >
                    <DatePicker format="DD/MM/YYYY" style={{ width: "100%" }} />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={[16, 0]}>
                <Col xs={24} sm={10} md={8}>
                  <Form.Item label="ลูกค้า" name="cusname">
                    <Input disabled />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={6} md={5}>
                  <Form.Item label="Brand" name="brand">
                    <Input disabled />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={8} md={11}>
                  <Form.Item label="หมายเหตุ" name="remark">
                    <Input placeholder="หมายเหตุ" />
                  </Form.Item>
                </Col>
                <Form.Item name="cuscode" hidden>
                  <Input />
                </Form.Item>
              </Row>
            </Form>

            <Flex justify="space-between" align="center">
              <Typography.Text strong>รายการสินค้า</Typography.Text>
              <Button
                size="small"
                type="primary"
                disabled={joItemsData.length === 0}
                onClick={() => setOpenJOItemsModal(true)}
              >
                + เพิ่มรายการ
              </Button>
            </Flex>
            <Table
              size="small"
              rowKey="seq"
              columns={detailCols}
              dataSource={detail}
              pagination={false}
              scroll={{ x: "max-content" }}
              summary={() => (
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0} colSpan={4} align="right">
                    <Typography.Text strong>รวม</Typography.Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={1} align="right">
                    <Typography.Text strong>{totalCarton.toLocaleString()}</Typography.Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={2} />
                </Table.Summary.Row>
              )}
            />
          </Space>
        )}
      </Spin>

      {openJOItemsModal && (
        <ModalJOItems
          show={openJOItemsModal}
          close={() => setOpenJOItemsModal(false)}
          joItems={joItemsData}
          onConfirm={handleConfirmJOItems}
        />
      )}
    </Modal>
  );
}
